/**
 * Derived Fields Calculation Utility
 *
 * This file contains logic for calculating additional personality fields
 * based on existing quiz responses. These derived fields are used for
 * pro features without requiring additional questions.
 */

/**
 * Calculate attachment style score based on existing personality traits
 * @param {Object} personalityScores - Object containing personality scores
 * @param {number} personalityScores.feeling - Feeling vs Thinking score (0-100)
 * @param {number} personalityScores.introversion - Introversion vs Extraversion score (0-100)
 * @param {number} personalityScores.perception - Perception vs Judging score (0-100)
 * @returns {number} Attachment score (0-100, where 100 = more anxious attachment)
 */
export const calculateAttachmentStyle = ({
  feeling,
  introversion,
  perception,
}) => {
  // Validate inputs
  if (
    feeling === undefined ||
    introversion === undefined ||
    perception === undefined
  ) {
    console.warn("Missing personality scores for attachment calculation");
    return 50; // Default to neutral
  }

  // Attachment calculation:
  // - Higher Feeling = More anxious (seeks emotional validation)
  // - Higher Introversion = More anxious (less secure in social situations)
  // - Lower Perception = More anxious (needs structure/predictability)
  const attachmentScore =
    feeling * 0.5 + // 50% weight - emotional sensitivity
    introversion * 0.3 + // 30% weight - social confidence
    (100 - perception) * 0.2; // 20% weight - need for structure

  // Ensure score is within 0-100 range
  return Math.max(0, Math.min(100, Math.round(attachmentScore)));
};

/**
 * Calculate risk tolerance score based on existing personality traits
 * @param {Object} personalityScores - Object containing personality scores
 * @param {number} personalityScores.perception - Perception vs Judging score (0-100)
 * @param {number} personalityScores.intuition - Intuition vs Sensing score (0-100)
 * @param {number} personalityScores.feeling - Feeling vs Thinking score (0-100)
 * @returns {number} Risk tolerance score (0-100, where 100 = more risk-taking)
 */
export const calculateRiskTolerance = ({ perception, intuition, feeling }) => {
  // Validate inputs
  if (
    perception === undefined ||
    intuition === undefined ||
    feeling === undefined
  ) {
    console.warn("Missing personality scores for risk tolerance calculation");
    return 50; // Default to neutral
  }

  // Risk tolerance calculation:
  // - Higher Perception = More risk-taking (comfortable with uncertainty)
  // - Higher Intuition = More risk-taking (trusts instincts over proven methods)
  // - Lower Feeling = More risk-taking (logic over emotional safety)
  const riskToleranceScore =
    perception * 0.4 + // 40% weight - comfort with uncertainty
    intuition * 0.4 + // 40% weight - trust in instincts
    (100 - feeling) * 0.2; // 20% weight - logical vs emotional decision making

  // Ensure score is within 0-100 range
  return Math.max(0, Math.min(100, Math.round(riskToleranceScore)));
};

/**
 * Calculate communication style for best-date quiz
 * @param {Object} dateScores - Object containing base dating scores
 * @returns {number} Communication score (0-100, where 100 = more verbal/direct)
 */
export const calculateCommunicationStyle = ({ open, simp, planner }) => {
  // Validate inputs
  if (open === undefined || simp === undefined || planner === undefined) {
    console.warn("Missing dating scores for communication calculation");
    return 50;
  }

  // Communication calculation:
  // - Higher Open = More direct communication
  // - Higher Simp = More direct/expressive communication
  // - Higher Planner = More structured/direct communication
  const communicationScore =
    open * 0.5 + // 50% weight - openness to expression
    simp * 0.3 + // 30% weight - willingness to express feelings
    planner * 0.2; // 20% weight - structured communication

  return Math.max(0, Math.min(100, Math.round(communicationScore)));
};

/**
 * Calculate investment level for best-date quiz
 * @param {Object} dateScores - Object containing base dating scores
 * @returns {number} Investment score (0-100, where 100 = high-effort)
 */
export const calculateInvestmentLevel = ({ boujee, planner, social }) => {
  if (boujee === undefined || planner === undefined || social === undefined) {
    console.warn("Missing dating scores for investment calculation");
    return 50;
  }

  // Investment calculation:
  // - Higher Boujee = More high-effort (likes fancy things)
  // - Higher Planner = More high-effort (puts thought into dates)
  // - Higher Social = More high-effort (social activities)
  const investmentScore =
    boujee * 0.4 + // 40% weight - preference for quality/luxury
    planner * 0.4 + // 40% weight - planning effort
    social * 0.2; // 20% weight - social activities

  return Math.max(0, Math.min(100, Math.round(investmentScore)));
};

/**
 * Calculate change approach for political quiz
 * @param {Object} politicalScores - Object containing base political scores
 * @returns {number} Change approach score (0-100, where 100 = revolutionary)
 */
export const calculateChangeApproach = ({
  socialLib,
  econLib,
  socialSupport,
}) => {
  if (
    socialLib === undefined ||
    econLib === undefined ||
    socialSupport === undefined
  ) {
    console.warn("Missing political scores for change approach calculation");
    return 50;
  }

  // Change approach calculation:
  // - Higher Social Liberalism = More revolutionary
  // - Higher Economic Liberalism = More revolutionary
  // - Higher Social Support = More systemic change oriented
  const changeScore =
    socialLib * 0.4 + // 40% weight - social progressiveness
    econLib * 0.3 + // 30% weight - economic progressiveness
    socialSupport * 0.3; // 30% weight - belief in systemic solutions

  return Math.max(0, Math.min(100, Math.round(changeScore)));
};

/**
 * Calculate authority view for political quiz
 * @param {Object} politicalScores - Object containing base political scores
 * @returns {number} Authority view score (0-100, where 100 = trusting)
 */
export const calculateAuthorityView = ({
  public: publicScore,
  socialSupport,
  globalism,
}) => {
  if (
    publicScore === undefined ||
    socialSupport === undefined ||
    globalism === undefined
  ) {
    console.warn("Missing political scores for authority view calculation");
    return 50;
  }

  // Authority view calculation:
  // - Higher Public Healthcare = More trusting of institutions
  // - Higher Social Support = More trusting of government
  // - Higher Globalism = More trusting of international institutions
  const authorityScore =
    publicScore * 0.4 + // 40% weight - trust in government systems
    socialSupport * 0.3 + // 30% weight - trust in government support
    globalism * 0.3; // 30% weight - trust in international institutions

  return Math.max(0, Math.min(100, Math.round(authorityScore)));
};

/**
 * Calculate sexual communication style for sex-styles quiz
 * @param {Object} sexScores - Object containing base sexuality scores
 * @returns {number} Communication style score (0-100, where 100 = verbal)
 */
export const calculateSexualCommunicationStyle = ({
  dom,
  exhibition,
  casual,
}) => {
  if (dom === undefined || exhibition === undefined || casual === undefined) {
    console.warn("Missing sexuality scores for communication calculation");
    return 50;
  }

  // Sexual communication calculation:
  // - Higher Dominance = More verbal communication
  // - Higher Exhibition = More verbal/expressive
  // - Lower Casual = More verbal (relationship-focused communication)
  const sexCommScore =
    dom * 0.4 + // 40% weight - dominance often involves verbal direction
    exhibition * 0.3 + // 30% weight - exhibitionists tend to be more verbal
    (100 - casual) * 0.3; // 30% weight - romantic vs casual affects communication

  return Math.max(0, Math.min(100, Math.round(sexCommScore)));
};

/**
 * Calculate sexual exploration for sex-styles quiz
 * @param {Object} sexScores - Object containing base sexuality scores
 * @returns {number} Exploration score (0-100, where 100 = experimental)
 */
export const calculateSexualExploration = ({ bdsm, voyeur, rough }) => {
  if (bdsm === undefined || voyeur === undefined || rough === undefined) {
    console.warn("Missing sexuality scores for exploration calculation");
    return 50;
  }

  // Sexual exploration calculation:
  // - Higher BDSM = More experimental
  // - Higher Voyeur = More experimental/adventurous
  // - Higher Rough = More experimental with intensity
  const explorationScore =
    bdsm * 0.4 + // 40% weight - BDSM indicates openness to exploration
    voyeur * 0.3 + // 30% weight - voyeurism indicates sexual curiosity
    rough * 0.3; // 30% weight - roughness indicates experimentation

  return Math.max(0, Math.min(100, Math.round(explorationScore)));
};

/**
 * Calculate all derived fields for personality quiz
 * @param {Object} personalityScores - Object containing base personality scores
 * @returns {Object} Object containing all derived field scores
 */
export const calculateDerivedPersonalityFields = (personalityScores) => {
  const { feeling, introversion, perception, intuition } = personalityScores;

  return {
    attachment: calculateAttachmentStyle({ feeling, introversion, perception }),
    riskTolerance: calculateRiskTolerance({ perception, intuition, feeling }),
  };
};

/**
 * Calculate attachment style category from attachment quiz scores
 * @param {Object} attachmentScores - Object containing attachment quiz scores
 * @param {number} attachmentScores.security - Security score (0-100, higher = more secure)
 * @param {number} attachmentScores.intimacy - Intimacy score (0-100, higher = more comfortable with intimacy)
 * @param {number} attachmentScores.conflict - Conflict score (0-100, lower = more avoidant/conflict-avoidant)
 * @param {number} attachmentScores.boundarySetting - Boundary setting score (0-100, lower = weaker boundaries = more anxious)
 * @param {number} attachmentScores.dependencyIndependence - Dependency score (0-100, higher = more dependent/anxious)
 * @returns {string} Attachment style: "Secure", "Anxious-Attached", or "Avoidant-Attached"
 */
export const calculateAttachmentStyleCategory = ({
  security,
  intimacy,
  conflict,
  boundarySetting,
  dependencyIndependence,
}) => {
  // Validate inputs
  if (
    security === undefined ||
    intimacy === undefined ||
    conflict === undefined ||
    boundarySetting === undefined ||
    dependencyIndependence === undefined
  ) {
    console.warn("Missing attachment scores for style calculation");
    return "SECURE"; // Default to secure
  }

  // Calculate all indicators with weighted scores
  let anxiousScore = 0;
  let avoidantScore = 0;
  let secureScore = 0;

  // ANXIOUS indicators (higher score = more anxious)
  if (dependencyIndependence >= 50) anxiousScore += 3; // Strong indicator
  if (boundarySetting < 40) anxiousScore += 2; // Medium indicator
  if (security < 60) anxiousScore += 1; // Weak indicator

  // AVOIDANT indicators (higher score = more avoidant)
  if (intimacy < 40) avoidantScore += 3; // Strong indicator
  if (intimacy < 50) avoidantScore += 1; // Weak indicator
  if (conflict <= 50) avoidantScore += 2; // Medium indicator

  // SECURE indicators (higher score = more secure)
  if (security >= 60) secureScore += 2; // Medium indicator
  if (intimacy >= 60) secureScore += 2; // Medium indicator
  if (boundarySetting >= 40) secureScore += 1; // Weak indicator

  // Priority-based decision logic
  // 1. Clear patterns (high confidence)
  if (anxiousScore >= 5) return "ANXIOUS"; // Strong anxious pattern
  if (avoidantScore >= 5) return "AVOIDANT"; // Strong avoidant pattern
  if (secureScore >= 5) return "SECURE"; // Strong secure pattern

  // 2. Moderate patterns (medium confidence)
  if (
    anxiousScore >= 3 &&
    anxiousScore > avoidantScore &&
    anxiousScore > secureScore
  )
    return "ANXIOUS";
  if (
    avoidantScore >= 3 &&
    avoidantScore > anxiousScore &&
    avoidantScore > secureScore
  )
    return "AVOIDANT";
  if (
    secureScore >= 3 &&
    secureScore > anxiousScore &&
    secureScore > avoidantScore
  )
    return "SECURE";

  // 3. Weak patterns or ties (low confidence)
  if (anxiousScore > avoidantScore && anxiousScore > secureScore)
    return "ANXIOUS";
  if (avoidantScore > anxiousScore && avoidantScore > secureScore)
    return "AVOIDANT";
  if (secureScore > anxiousScore && secureScore > avoidantScore)
    return "SECURE";

  // 4. Complete tie - default to secure
  return "SECURE";
};

/**
 * Calculate political alignment category based on quiz scores
 * @param {Object} scores - Object containing political quiz scores
 * @param {number} scores.socialLib - Social liberalism score (0-100)
 * @param {number} scores.econLib - Economic liberalism score (0-100)
 * @param {number} scores.globalism - Globalism score (0-100)
 * @param {number} scores.conservation - Conservation score (0-100)
 * @param {number} scores.socialSupport - Social support score (0-100)
 * @param {number} scores.public - Public sector score (0-100)
 * @returns {string} - "Liberal", "Moderate", or "Conservative"
 */
export const calculatePoliticalAlignmentCategory = ({
  socialLib,
  econLib,
  globalism,
  conservation,
  socialSupport,
  public: publicSector,
}) => {
  // Validate inputs
  if (
    socialLib === undefined ||
    econLib === undefined ||
    globalism === undefined ||
    conservation === undefined ||
    socialSupport === undefined ||
    publicSector === undefined
  ) {
    console.warn("Missing political scores for alignment calculation");
    return "Moderate"; // Default to moderate
  }

  // Calculate weighted scores for each alignment
  let liberalScore = 0;
  let conservativeScore = 0;

  // LIBERAL indicators (higher scores = more liberal)
  // Social liberalism: higher scores indicate more liberal social views
  if (socialLib >= 70) liberalScore += 3; // Strong indicator
  else if (socialLib >= 60) liberalScore += 2; // Medium indicator
  else if (socialLib >= 50) liberalScore += 1; // Weak indicator

  // Economic liberalism: higher scores indicate more liberal economic views
  if (econLib >= 70) liberalScore += 3; // Strong indicator
  else if (econLib >= 60) liberalScore += 2; // Medium indicator
  else if (econLib >= 50) liberalScore += 1; // Weak indicator

  // Globalism: higher scores indicate more globalist/internationalist views
  if (globalism >= 70) liberalScore += 2; // Medium indicator
  else if (globalism >= 60) liberalScore += 1; // Weak indicator

  // Social support: higher scores indicate more support for social programs
  if (socialSupport >= 70) liberalScore += 2; // Medium indicator
  else if (socialSupport >= 60) liberalScore += 1; // Weak indicator

  // Public sector: higher scores indicate more government involvement
  if (publicSector >= 70) liberalScore += 2; // Medium indicator
  else if (publicSector >= 60) liberalScore += 1; // Weak indicator

  // Conservation: higher scores indicate more environmentalism (generally liberal)
  if (conservation >= 70) liberalScore += 1; // Weak indicator
  else if (conservation >= 60) liberalScore += 0.5; // Very weak indicator

  // CONSERVATIVE indicators (lower scores = more conservative)
  // Social liberalism: lower scores indicate more conservative social views
  if (socialLib <= 30) conservativeScore += 3; // Strong indicator
  else if (socialLib <= 40) conservativeScore += 2; // Medium indicator
  else if (socialLib <= 50) conservativeScore += 1; // Weak indicator

  // Economic liberalism: lower scores indicate more conservative economic views
  if (econLib <= 30) conservativeScore += 3; // Strong indicator
  else if (econLib <= 40) conservativeScore += 2; // Medium indicator
  else if (econLib <= 50) conservativeScore += 1; // Weak indicator

  // Globalism: lower scores indicate more nationalist/isolationist views
  if (globalism <= 30) conservativeScore += 2; // Medium indicator
  else if (globalism <= 40) conservativeScore += 1; // Weak indicator

  // Social support: lower scores indicate less support for social programs
  if (socialSupport <= 30) conservativeScore += 2; // Medium indicator
  else if (socialSupport <= 40) conservativeScore += 1; // Weak indicator

  // Public sector: lower scores indicate less government involvement
  if (publicSector <= 30) conservativeScore += 2; // Medium indicator
  else if (publicSector <= 40) conservativeScore += 1; // Weak indicator

  // Conservation: lower scores indicate less environmentalism (but this is less clear-cut)
  if (conservation <= 30) conservativeScore += 1; // Weak indicator
  else if (conservation <= 40) conservativeScore += 0.5; // Very weak indicator

  // Priority-based decision logic
  // 1. Clear patterns (high confidence)
  if (liberalScore >= 8) return "Liberal"; // Strong liberal pattern
  if (conservativeScore >= 8) return "Conservative"; // Strong conservative pattern

  // 2. Moderate patterns (medium confidence)
  if (liberalScore >= 5 && liberalScore > conservativeScore) return "Liberal";
  if (conservativeScore >= 5 && conservativeScore > liberalScore)
    return "Conservative";

  // 3. Weak patterns or ties (low confidence) - default to moderate
  if (liberalScore > conservativeScore) return "Liberal";
  if (conservativeScore > liberalScore) return "Conservative";

  // 4. Complete tie - default to moderate
  return "Moderate";
};

/**
 * Calculate all derived fields for best-date quiz
 * @param {Object} dateScores - Object containing base dating scores
 * @returns {Object} Object containing all derived field scores
 */
export const calculateDerivedDateFields = (dateScores) => {
  const { open, simp, planner, boujee, social } = dateScores;

  return {
    communication: calculateCommunicationStyle({ open, simp, planner }),
    investment: calculateInvestmentLevel({ boujee, planner, social }),
  };
};

/**
 * Calculate all derived fields for political-alignment quiz
 * @param {Object} politicalScores - Object containing base political scores
 * @returns {Object} Object containing all derived field scores
 */
export const calculateDerivedPoliticalFields = (politicalScores) => {
  const {
    socialLib,
    econLib,
    socialSupport,
    public: publicScore,
    globalism,
  } = politicalScores;

  return {
    changeApproach: calculateChangeApproach({
      socialLib,
      econLib,
      socialSupport,
    }),
    authorityView: calculateAuthorityView({
      public: publicScore,
      socialSupport,
      globalism,
    }),
  };
};

/**
 * Calculate all derived fields for sex-styles quiz
 * @param {Object} sexScores - Object containing base sexuality scores
 * @returns {Object} Object containing all derived field scores
 */
export const calculateDerivedSexFields = (sexScores) => {
  const { dom, exhibition, casual, bdsm, voyeur, rough } = sexScores;

  return {
    communicationStyle: calculateSexualCommunicationStyle({
      dom,
      exhibition,
      casual,
    }),
    exploration: calculateSexualExploration({ bdsm, voyeur, rough }),
  };
};

/**
 * Calculate derived fields for any quiz type
 * @param {string} quizType - The type of quiz (e.g., 'personality-check')
 * @param {Object} baseScores - Object containing base quiz scores
 * @returns {Object} Object containing derived field scores for the quiz type
 */
export const calculateDerivedFields = (quizType, baseScores) => {
  switch (quizType) {
    case "personality-check":
      return calculateDerivedPersonalityFields(baseScores);

    case "best-date":
      return calculateDerivedDateFields(baseScores);

    case "political-alignment":
      return calculateDerivedPoliticalFields(baseScores);

    case "sex-styles":
      return calculateDerivedSexFields(baseScores);

    case "am-i-gay":
      // Provide a synthetic overall axis for Results (top bar)
      return { kinseyOverall: calculateKinseyOverallPercent(baseScores) };

    default:
      console.warn(
        `No derived field calculations defined for quiz type: ${quizType}`
      );
      return {};
  }
};

/**
 * Debug function to log calculation details
 * @param {string} quizType - The quiz type
 * @param {Object} baseScores - Base scores
 * @param {Object} derivedScores - Calculated derived scores
 */
export const logDerivedFieldCalculation = (
  quizType,
  baseScores,
  derivedScores
) => {};

/**
 * Kinsey helpers for Am-I-Gay quiz
 */
export const calculateKinseyNumber = (kinseyScore /* 0-100 */) => {
  if (typeof kinseyScore !== "number" || isNaN(kinseyScore)) return 0;
  const n = Math.round((Math.max(0, Math.min(100, kinseyScore)) / 100) * 6);
  return Math.max(0, Math.min(6, n));
};

export const getKinseyLabel = (num /* 0-6 */) => {
  switch (num) {
    case 0:
      return "Totally Straight";
    case 1:
      return "Mostly Straight";
    case 2:
      return "Leans Straight";
    case 3:
      return "Bisexual / Fluid";
    case 4:
      return "Leans Gay";
    case 5:
      return "Mostly Gay";
    case 6:
      return "Totally Gay";
    default:
      return "Unknown";
  }
};

export const calculateKinseyResult = (scores) => {
  // If an overall kinsey score exists, use it
  if (scores && typeof scores.kinsey === "number") {
    const number = calculateKinseyNumber(scores.kinsey);
    return { number, label: getKinseyLabel(number) };
  }

  // Otherwise average across recognized Kinsey-related fields
  const recognizedFields = [
    "same-sex", // Physical Attraction
    "same-gender", // Emotional Attraction
    "queer-leaning", // Identity
    "queer-inclusive", // Past Experiences
    "explorative", // Curiosity
    "at-ease", // Social-comfort (if added later)
  ];

  const values = recognizedFields
    .map((key) => (typeof scores?.[key] === "number" ? scores[key] : null))
    .filter((v) => v !== null);

  const avg = values.length
    ? Math.round(values.reduce((a, b) => a + b, 0) / values.length)
    : 0;

  const number = calculateKinseyNumber(avg);
  return { number, label: getKinseyLabel(number) };
};

// Calculate overall Kinsey percent from base fields (0-100)
const calculateKinseyOverallPercent = (scores) => {
  if (!scores) return 0;
  const recognizedFields = [
    "same-sex",
    "same-gender",
    "queer-leaning",
    "queer-inclusive",
    "explorative",
    "at-ease",
  ];
  const values = recognizedFields
    .map((k) => (typeof scores[k] === "number" ? scores[k] : null))
    .filter((v) => v !== null);
  if (values.length === 0) return 0;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
};

/**
 * Love Language type mapping
 */
const loveLanguageTypes = {
  serviceOriented: {
    title: "Acts of Service",
    description:
      "You show love by doing — little favors, helping out, showing up when it counts.",
  },
  present: {
    title: "Quality Time",
    description:
      "You value being fully present — deep talks, shared moments, no distractions.",
  },
  touchy: {
    title: "Physical Touch",
    description:
      "You speak through touch — hugs, hand-holding, leaning in close when words aren't enough.",
  },
  thoughtful: {
    title: "Receiving Gifts",
    description:
      'You express love through thoughtfulness — small surprises that say "I see you."',
  },
  vocal: {
    title: "Words of Affirmation",
    description:
      'You show love through words — compliments, encouragement, late-night "just thinking of you" texts.',
  },
};

/**
 * Calculate dominant love language type from quiz scores
 * @param {Object} scores - Object containing love language quiz scores
 * @returns {Object} - { title, description } of the dominant love language
 */
export const calculateLoveLanguageType = (scores) => {
  if (!scores) {
    return loveLanguageTypes.vocal; // Default
  }

  // Only consider the 5 core love language fields
  const coreFields = [
    "serviceOriented",
    "present",
    "touchy",
    "thoughtful",
    "vocal",
  ];

  let highestField = null;
  let highestScore = -1;

  coreFields.forEach((field) => {
    const score = scores[field];
    if (typeof score === "number" && score > highestScore) {
      highestScore = score;
      highestField = field;
    }
  });

  if (highestField && loveLanguageTypes[highestField]) {
    return loveLanguageTypes[highestField];
  }

  return loveLanguageTypes.vocal; // Default fallback
};

/**
 * ============================================================================
 * GENERIC SUMMARY CALCULATOR
 *
 * This allows remote quizzes to configure their top label/summary display
 * without requiring code changes.
 *
 * summaryConfig structure:
 * {
 *   type: "traitMapping" | "highestAxis" | "weightedCategory" | "none",
 *
 *   // For "traitMapping" type:
 *   traitMapping: { field: { high: "X", low: "Y" }, ... }
 *
 *   // For "highestAxis" type:
 *   axisLabels: { field: "Display Label", ... }
 *   fieldsToConsider: ["field1", "field2", ...]  // optional, defaults to all axisLabels keys
 *
 *   // For "weightedCategory" type:
 *   categories: [
 *     { name: "Category1", thresholds: { field1: { min: 60 }, field2: { min: 50 } } },
 *     { name: "Category2", thresholds: { field1: { max: 40 }, field2: { max: 50 } } },
 *   ],
 *   defaultCategory: "Default"
 * }
 * ============================================================================
 */

/**
 * Calculate summary label based on summaryConfig
 * @param {Object} summaryConfig - Configuration for summary calculation
 * @param {Object} scores - Object containing quiz scores by field
 * @param {Array} percentages - Array of percentage objects (for traitMapping)
 * @returns {string|null} The calculated summary label or null if type is "none"
 */
export const calculateDynamicSummary = (
  summaryConfig,
  scores,
  percentages = []
) => {
  if (!summaryConfig || summaryConfig.type === "none") {
    return null;
  }

  switch (summaryConfig.type) {
    case "traitMapping":
      return calculateTraitMappingSummary(
        summaryConfig.traitMapping,
        percentages,
        scores
      );

    case "highestAxis":
      return calculateHighestAxisSummary(summaryConfig, scores);

    case "weightedCategory":
      return calculateWeightedCategorySummary(summaryConfig, scores);

    default:
      console.warn(`Unknown summary type: ${summaryConfig.type}`);
      return null;
  }
};

/**
 * Calculate summary using trait mapping (like ENFP for personality)
 * @param {Object} traitMapping - The trait mapping config
 * @param {Array} percentages - Percentage array for trait mapping
 * @param {Object} scores - Optional scores object (used for second user in shared quizzes)
 */
const calculateTraitMappingSummary = (
  traitMapping,
  percentages,
  scores = null
) => {
  if (!traitMapping || !percentages || percentages.length === 0) {
    return null;
  }

  let result = "";

  percentages.forEach((item) => {
    const mapping = traitMapping[item.field];
    if (mapping) {
      // If scores object is provided, use it (for second user in shared quizzes)
      // Otherwise fall back to percentage1 from the percentages array
      const percentage =
        scores && scores[item.field] !== undefined
          ? scores[item.field]
          : item.percentage1 ?? item.percentage;
      if (typeof percentage === "number") {
        result += percentage > 50 ? mapping.high : mapping.low;
      }
    }
  });

  return result || null;
};

/**
 * Calculate summary using highest axis (like Love Languages)
 */
const calculateHighestAxisSummary = (summaryConfig, scores) => {
  if (!summaryConfig.axisLabels || !scores) {
    return null;
  }

  const fieldsToConsider =
    summaryConfig.fieldsToConsider || Object.keys(summaryConfig.axisLabels);

  let highestField = null;
  let highestScore = -1;

  fieldsToConsider.forEach((field) => {
    const score = scores[field];
    if (typeof score === "number" && score > highestScore) {
      highestScore = score;
      highestField = field;
    }
  });

  if (highestField && summaryConfig.axisLabels[highestField]) {
    return summaryConfig.axisLabels[highestField];
  }

  return summaryConfig.defaultLabel || null;
};

/**
 * Calculate summary using weighted category scoring (like Political Alignment)
 */
const calculateWeightedCategorySummary = (summaryConfig, scores) => {
  if (!summaryConfig.categories || !scores) {
    return summaryConfig.defaultCategory || null;
  }

  // Calculate scores for each category based on thresholds
  const categoryScores = {};

  summaryConfig.categories.forEach((category) => {
    let score = 0;
    const thresholds = category.thresholds || {};

    Object.entries(thresholds).forEach(([field, threshold]) => {
      const fieldScore = scores[field];
      if (typeof fieldScore !== "number") return;

      const weight = threshold.weight || 1;

      // Check if score meets threshold conditions
      if (threshold.min !== undefined && fieldScore >= threshold.min) {
        score += weight;
      }
      if (threshold.max !== undefined && fieldScore <= threshold.max) {
        score += weight;
      }
      // Range check: score must be between min and max
      if (threshold.min !== undefined && threshold.max !== undefined) {
        if (fieldScore >= threshold.min && fieldScore <= threshold.max) {
          score += weight;
        }
      }
    });

    categoryScores[category.name] = score;
  });

  // Find category with highest score
  let bestCategory = summaryConfig.defaultCategory || null;
  let bestScore = summaryConfig.minScoreToShow || 0;

  Object.entries(categoryScores).forEach(([category, score]) => {
    if (score > bestScore) {
      bestScore = score;
      bestCategory = category;
    }
  });

  return bestCategory;
};

/**
 * Get summary for a quiz, using either legacy hardcoded logic or dynamic config
 * This provides backwards compatibility while supporting new remote quizzes
 *
 * @param {Object} quizConfig - The quiz configuration
 * @param {Object} scores - Quiz answer scores by field
 * @param {Array} percentages - Percentage array for trait mapping
 * @returns {string|null} The summary label
 */
export const getQuizSummary = (quizConfig, scores, percentages = []) => {
  // Check for new summaryConfig first
  if (quizConfig?.results?.summaryConfig) {
    return calculateDynamicSummary(
      quizConfig.results.summaryConfig,
      scores,
      percentages
    );
  }

  // Legacy: Check for traitMapping (personality-check style)
  if (quizConfig?.results?.traitMapping) {
    return calculateTraitMappingSummary(
      quizConfig.results.traitMapping,
      percentages,
      scores
    );
  }

  // No summary config - return null (caller should use hardcoded logic if needed)
  return null;
};
